/** Default user-facing copy when the API body has no usable `message` (French UI). */
const STATUS_FALLBACK: Record<number, string> = {
  400: "Requête invalide.",
  401: "Session expirée ou non autorisé.",
  403: "Accès refusé — droits insuffisants.",
  404: "Ressource introuvable.",
  409: "Conflit — cette action n’est pas possible.",
  422: "Données invalides.",
  429: "Trop de requêtes. Réessaie plus tard.",
};

function pickMessageFromJson(body: unknown): string | undefined {
  if (body == null || typeof body !== "object") return undefined;
  const o = body as Record<string, unknown>;
  for (const key of ["message", "error", "detail"] as const) {
    const v = o[key];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return undefined;
}

function pickCodeFromJson(body: unknown): string | undefined {
  if (body == null || typeof body !== "object") return undefined;
  const c = (body as Record<string, unknown>).code;
  return typeof c === "string" && c.trim() ? c.trim() : undefined;
}

export type ParsedApiError = {
  status: number;
  message: string;
  /** Present when the API returns a stable machine code (optional contract). */
  code?: string;
};

export function formatHttpErrorMessage(
  status: number,
  parsedBody: unknown,
  nonJsonBodyText?: string,
): string {
  const fromJson = pickMessageFromJson(parsedBody);
  if (fromJson) return fromJson;
  const t = nonJsonBodyText?.trim();
  if (t && t.length > 0 && t.length < 500) return t;
  return STATUS_FALLBACK[status] || `Erreur HTTP ${status}`;
}

/**
 * Single read of a non-OK response body; use this before building {@link HttpError}.
 */
export async function parseErrorFromResponse(response: Response): Promise<ParsedApiError> {
  const status = response.status;
  let rawText: string;
  try {
    rawText = await response.text();
  } catch {
    return { status, message: formatHttpErrorMessage(status, undefined) };
  }
  if (!rawText) {
    return { status, message: formatHttpErrorMessage(status, undefined) };
  }
  try {
    const body = JSON.parse(rawText) as unknown;
    return {
      status,
      code: pickCodeFromJson(body),
      message: formatHttpErrorMessage(status, body),
    };
  } catch {
    return {
      status,
      message: formatHttpErrorMessage(status, undefined, rawText),
    };
  }
}

/**
 * Typed failure for any `fetch` where the body was parsed by {@link parseErrorFromResponse}.
 * Contexts can branch on `status` (401 vs 403) without string-matching `message`.
 */
export class HttpError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(parsed: ParsedApiError) {
    super(parsed.message);
    this.name = "HttpError";
    this.status = parsed.status;
    this.code = parsed.code;
  }
}

export async function assertResponseOk(response: Response): Promise<void> {
  if (response.ok) return;
  throw new HttpError(await parseErrorFromResponse(response));
}

/** Success when status is one of `allowed` (e.g. 204 only, or 200|204). Otherwise throws {@link HttpError}. */
export async function expectStatuses(response: Response, ...allowed: number[]): Promise<void> {
  if (allowed.includes(response.status)) return;
  if (!response.ok) throw new HttpError(await parseErrorFromResponse(response));
  throw new HttpError({
    status: response.status,
    message: formatHttpErrorMessage(response.status, undefined),
  });
}

const defaultInit: RequestInit = { credentials: "include" };

/** Session cookies + shared defaults; all Zekay API calls should go through here over time. */
export function fetchZekay(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  return fetch(input, { ...defaultInit, ...init });
}

/**
 * GET/POST JSON success path: throws {@link HttpError} on non-OK.
 * 204 → `undefined` as `T` (caller should type as `void` or optional).
 */
export async function fetchZekayJson<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
  const res = await fetchZekay(input, init);
  await assertResponseOk(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
